import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardMedia,
  IconButton,
  Button
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  DirectionsCar as CarIcon,
  PhotoCamera as PhotoCameraIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { getTransformedImageUrl } from '../../../utils/filestack';

const CarDetailsCard = ({ 
  car, 
  isCarOwner, 
  handleImageUpload, 
  handleImageError, 
  setEditDialogOpen, 
  setDeleteDialogOpen 
}) => {
  const { t } = useTranslation('cars');

  return (
    <Card elevation={3}>
      <Box position="relative">
        {car.imageUrl ? (
          <CardMedia
            component="img"
            image={getTransformedImageUrl(car.imageUrl, 'app')}
            alt={car.name}
            onError={handleImageError}
          />
        ) : (
          <Box 
            sx={{ 
              height: 200, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              bgcolor: 'background.paper'
            }}
          >
            <CarIcon sx={{ fontSize: 80, color: 'text.secondary' }} />
          </Box>
        )}
        
        {isCarOwner() && (
          <Box 
            position="absolute" 
            top={8} 
            right={8}
            sx={{ 
              display: 'flex', 
              gap: 1,
              bgcolor: 'rgba(0,0,0,0.5)',
              borderRadius: 1,
              p: 0.5
            }}
          >
            <IconButton 
              size="small" 
              onClick={() => setEditDialogOpen(true)}
              sx={{ color: 'white' }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton 
              size="small" 
              onClick={() => setDeleteDialogOpen(true)}
              sx={{ color: 'white' }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
        
        {isCarOwner() && (
          <Box 
            position="absolute" 
            bottom={8} 
            right={8}
          >
            <Button
              variant="contained"
              size="small"
              startIcon={<PhotoCameraIcon />}
              onClick={handleImageUpload}
            >
              {t('cars:upload.changeImage')}
            </Button>
            <input
              type="file"
              id="car-image-upload"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleImageUpload}
            />
          </Box>
        )}
      </Box>
      
      <CardContent>
        {/* Car details */}
        <Box mb={2}>
          <Typography variant="subtitle2" color="text.secondary">
            {t('carDetail.name')}
          </Typography>
          <Typography variant="h6">{car.name}</Typography>
        </Box>
        
        {car.model && (
          <Box mb={2}>
            <Typography variant="subtitle2" color="text.secondary">
              {t('carDetail.model')}
            </Typography>
            <Typography variant="body1">{car.model}</Typography>
          </Box>
        )}
        
        {car.year && (
          <Box mb={2}>
            <Typography variant="subtitle2" color="text.secondary">
              {t('carDetail.year')}
            </Typography>
            <Typography variant="body1">{car.year}</Typography>
          </Box>
        )}
        
        {car.user && car.user.name && (
          <Box mb={2}>
            <Typography variant="subtitle2" color="text.secondary">
              {t('carDetail.owner')}
            </Typography>
            <Typography variant="body1">{car.user.name}</Typography>
          </Box>
        )}
        
        {car.notes && (
          <Box mb={2}>
            <Typography variant="subtitle2" color="text.secondary">
              {t('carDetail.notes')}
            </Typography>
            <Typography variant="body1">{car.notes}</Typography>
          </Box>
        )}
        
        {/* Component Notes */}
        {car.components && Object.entries(car.components).some(([_, component]) => component.notes) && (
          <Box>
            <Typography variant="subtitle2" color="text.secondary" fontWeight="bold" sx={{ mt: 2, mb: 1 }}>
              {t('carDetail.componentNotes')}
            </Typography>
            
            {Object.entries(car.components).map(([componentKey, component]) => (
              component.notes && (
                <Box key={componentKey} mb={2}>
                  <Typography variant="subtitle2" color="primary">
                    {t(`carDetail.components.${componentKey}`)}
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', ml: 1 }}>
                    {component.notes}
                  </Typography>
                </Box>
              )
            ))}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default CarDetailsCard; 